// src/services/custom-credentials-provider.js
import { type CredentialsAndIdentityIdProvider, type CredentialsAndIdentityId, type GetCredentialsOptions } from 'aws-amplify/auth';
import { CognitoIdentity } from '@aws-sdk/client-cognito-identity';
import { ConfigService } from './ConfigService';

// Initialize variables that will be set after configuration is loaded
let cognitoRegion = '';
let cognitoIdentityPoolId = '';
let cognitoidentity: CognitoIdentity;

// Singleton implementation of the custom credentials provider
class CustomCredentialsProvider implements CredentialsAndIdentityIdProvider {
  // Class member to hold the login information
  federatedLogin:any;
  
  // Cache for credentials to avoid unnecessary API calls
  cachedCredentials:any;
  
  // Singleton instance
  static instance:any;
  
  constructor() {
    // Ensure only one instance exists
    if (CustomCredentialsProvider.instance) {
      return CustomCredentialsProvider.instance;
    }
    
    this.federatedLogin = null;
    this.cachedCredentials = null;
    CustomCredentialsProvider.instance = this;
  }

  // Initialize the credentials provider with configuration
  static async initialize() {
    try {
      // Load configuration from ConfigService
      const config = await ConfigService.loadConfig(null, null);
      
      if (!config || !config.region || !config.identityPoolId || !config.userPoolClientId || !config.userPoolId) {
        throw new Error('Missing Cognito configuration in app config');
      }
      
      cognitoIdentityPoolId = config.identityPoolId;
      cognitoRegion = config.region;
      cognitoidentity = new CognitoIdentity({ region: cognitoRegion });
      
      return config;
    } catch (error) {
      console.error('Failed to initialize CustomCredentialsProvider:', error);
      throw error;
    }
  }
  
  // Method to load federation login information
  loadFederatedLogin(login:any) {
    this.federatedLogin = login;
    // Clear cached credentials when login changes
    this.cachedCredentials = null;
  }
  
  // Get credentials and identity ID from Cognito Identity Pool
  async getCredentialsAndIdentityId(
    getCredentialsOptions: GetCredentialsOptions
  ): Promise<CredentialsAndIdentityId | undefined> {
    try {
      // Ensure that we have initialized the credentials provider
      if (!cognitoidentity) {
        throw new Error('CustomCredentialsProvider not initialized. Call initialize() first.');
      }
      
      // Check if we have valid cached credentials
      if (this.cachedCredentials && this.cachedCredentials.credentials.expiration) {
        const now = new Date();
        const expiration = new Date(this.cachedCredentials.credentials.expiration);
        
        // Use cached credentials if they're not expired (with 5 minute buffer)
        if (expiration.getTime() - now.getTime() > 5 * 60 * 1000) {
          return this.cachedCredentials;
        }
      }
      
      // Validate that we have login information
      if (!this.federatedLogin || !this.federatedLogin.domain || !this.federatedLogin.token) {
        console.error('No federated login information available');
        return undefined;
      }
      
      // Get identity ID from Cognito. A 400 here almost always means a stale
      // cached idToken (user signed out/in, token refreshed) — drop the cache
      // and retry once with a fresh session before surfacing the error.
      let getIdResult;
      try {
        getIdResult = await cognitoidentity.getId({
          IdentityPoolId: cognitoIdentityPoolId,
          Logins: {
            [this.federatedLogin.domain]: this.federatedLogin.token
          },
        });
      } catch (firstError: any) {
        const status = firstError?.$metadata?.httpStatusCode;
        if (status !== 400 && firstError?.name !== 'NotAuthorizedException') throw firstError;
        console.warn('Cognito getId 400 — refreshing session and retrying once');
        this.cachedCredentials = null;
        try {
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession({ forceRefresh: true });
          const token = session.tokens?.idToken?.toString();
          if (token) this.federatedLogin = { ...this.federatedLogin, token };
        } catch { /* fall through to retry with what we have */ }
        getIdResult = await cognitoidentity.getId({
          IdentityPoolId: cognitoIdentityPoolId,
          Logins: {
            [this.federatedLogin.domain]: this.federatedLogin.token
          },
        });
      }
      
      if (!getIdResult.IdentityId) {
        throw new Error('Failed to get identity ID from Cognito');
      }
      
      // Get credentials for identity ID
      const cognitoCredentialsResult = await cognitoidentity.getCredentialsForIdentity({
        IdentityId: getIdResult.IdentityId,
        Logins: { 
          [this.federatedLogin.domain]: this.federatedLogin.token 
        },
      });
      
      if (!cognitoCredentialsResult.Credentials) {
        throw new Error('Failed to get credentials from Cognito');
      }
      
      // Format credentials
      const credentials: CredentialsAndIdentityId = {
        credentials: {
          accessKeyId: cognitoCredentialsResult.Credentials.AccessKeyId || '',
          secretAccessKey: cognitoCredentialsResult.Credentials.SecretKey || '',
          sessionToken: cognitoCredentialsResult.Credentials.SessionToken,
          expiration: cognitoCredentialsResult.Credentials.Expiration,
        },
        identityId: getIdResult.IdentityId,
      };
      
      // Cache the credentials
      this.cachedCredentials = credentials;
      
      return credentials;
    } catch (error) {
      // Real failures (Cognito outage, bad token, uninitialized provider) must
      // propagate — returning undefined here makes every downstream Amplify
      // call fail with an opaque "no credentials" instead of the true cause.
      // The legitimate unauthenticated case returns early above.
      console.error('Error getting credentials:', error);
      throw error;
    }
  }
  
  // Clear cached credentials and identity ID when logging out
  clearCredentialsAndIdentityId(): void {
    this.federatedLogin = null;
    this.cachedCredentials = null;
  }
}

// Export a singleton instance
export { CustomCredentialsProvider };

// Export a function to get the singleton instance
export const getCredentialsProvider = () => new CustomCredentialsProvider();